import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { Plus, Search, Copy, AlertTriangle, Sparkles, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useApp } from '@/context/AppContext';

export default function NurseQueue() {
  const { packetTypes, user } = useApp();
  // `send_intake_links` gates creating and resending patient links on the server.
  // Only nurses and admins reach this page, and both default it ON, so the
  // effective value is simply "not explicitly switched off" — no copy of the
  // role-default table is needed here.
  const canSendLinks = user?.permissions?.send_intake_links !== false;
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [packetTypeId, setPacketTypeId] = useState('fmla_std');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get('/nurse/queue', { params: { status, q } });
    setItems(r.data || []);
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const r = await api.post('/submissions/magic-link', { packet_type_id: packetTypeId, patient_email: patientEmail, patient_phone: patientPhone, sms_consent: smsConsent });
    const fullLink = `${window.location.origin}/p/${r.data.magic_token}`;
    try { await navigator.clipboard.writeText(fullLink); toast.success('Magic link copied to clipboard'); } catch { toast.success('Magic link created'); }
    const er = r.data.email_result;
    if (er?.event === 'email_sent') {
      toast.success(`Intake link emailed to ${er.to}`);
    } else if (er?.event === 'email_simulated') {
      toast.warning('Link created — the email was NOT sent', {
        description: 'No email provider is configured on this deployment, so the send was simulated. Copy the link and share it manually.',
      });
    } else if (er?.event === 'email_failed') {
      toast.warning('Link created, but the email could not be delivered', {
        description: 'Copy the link and share it manually. During the pilot, emails can only be delivered to the practice\u2019s verified address until a sending domain is verified.',
      });
    }
    const sr = r.data.sms_result;
    if (sr?.event === 'sms_sent') {
      toast.success(`Intake link texted to ${sr.to}`);
    } else if (sr?.event === 'sms_simulated') {
      toast.warning('Link created — the text was NOT sent', {
        description: 'No SMS provider is configured on this deployment, so the send was simulated. Copy the link and share it manually.',
      });
    } else if (sr?.event === 'sms_failed') {
      toast.warning('Link created, but the text message could not be delivered', {
        description: 'Copy the link and share it manually. On a Twilio trial, texts only deliver to verified numbers.',
      });
    }
    setPatientEmail('');
    setPatientPhone('');
    setOpen(false);
    load();
  };

  const copy = async (token) => {
    const link = `${window.location.origin}/p/${token}`;
    try { await navigator.clipboard.writeText(link); toast.success('Link copied'); } catch { toast.message(link); }
  };

  // Send the intake link again and give it another seven days. Same token, so a
  // link already in the patient's inbox starts working again.
  const resend = async (it) => {
    try {
      const r = await api.post(`/submissions/${it.id}/magic-link/resend`);
      const er = r.data.email_result; const sr = r.data.sms_result;
      if (er?.event === 'email_sent' || sr?.event === 'sms_sent') toast.success('Link re-sent — it is valid for another 7 days');
      else if (!er && !sr) toast.message('Link renewed for another 7 days', { description: 'No email or mobile is on file for this patient — copy the link and share it yourself.' });
      else toast.warning('Link renewed for another 7 days, but it could not be delivered', { description: 'Copy the link and share it manually.' });
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Submission Queue</h1>
        <span className="text-xs text-muted-foreground">{items.length} item(s)</span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => navigate('/nurse/fast-review')} data-testid="nurse-fast-review-mode-button">
          <Sparkles className="h-4 w-4 mr-1.5" /> Fast Review
        </Button>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input className="pl-7 w-56" placeholder="Search patient / employer / id" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="nurse-queue-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="nurse-queue-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft_patient">Waiting on patient</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_review">In review</SelectItem>
            <SelectItem value="awaiting_doctor">Awaiting doctor</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="declined">Not certified</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {canSendLinks && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="nurse-create-magic-link-button"><Plus className="h-4 w-4 mr-1" /> New patient link</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create patient magic link</DialogTitle><DialogDescription>Generates a unique 7-day link the patient can use to fill in their portion of the packet.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Packet type</label>
                <Select value={packetTypeId} onValueChange={setPacketTypeId}>
                  <SelectTrigger data-testid="nurse-create-packet-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {packetTypes.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Patient email (optional)</label>
                <Input value={patientEmail} onChange={(e) => setPatientEmail(e.target.value)} placeholder="patient@example.com" data-testid="nurse-create-patient-email" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Patient mobile (optional — texts the link)</label>
                <Input type="tel" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} placeholder="+1 555 123 4567" data-testid="nurse-create-patient-phone" />
                {patientPhone.trim() && (
                  <label className="mt-2 flex items-start gap-2 text-xs">
                    <input type="checkbox" className="mt-0.5" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} data-testid="nurse-create-sms-consent" />
                    <span>The patient agreed to receive text messages about this request. Msg &amp; data rates may apply; reply STOP to opt out.</span>
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={!!patientPhone.trim() && !smsConsent} data-testid="nurse-create-confirm-button">Create & copy link</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3" data-testid="nurse-queue-cards">
        {items.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground">No submissions yet. Create a patient link to get started.</Card>
        )}
        {items.map((it) => {
          const pt = packetTypes.find(p => p.id === it.packet_type_id)?.label || it.packet_type_id;
          const due = it.delivery?.due_date || '';
          const overdue = it._overdue;
          return (
            <Card key={it.id} className={`p-4 space-y-2 ${overdue ? 'border-l-2 border-l-[hsl(0_72%_52%)]' : ''}`} data-testid={`nurse-queue-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{it.patient_form?.full_name || '(awaiting patient)'}</p>
                  <p className="text-xs text-muted-foreground truncate">{it.patient_form?.employer_name || pt}</p>
                </div>
                <StatusBadge status={it.status} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {it.auto_prep?.status === 'done' && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-semibold"><Sparkles className="h-3 w-3" /> AI-prepped</span>
                )}
                {(it._open_revisions || 0) > 0 && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] px-1.5 py-0.5 text-[10px] font-semibold"><AlertTriangle className="h-3 w-3" /> Change requested</span>
                )}
                {due && (
                  <span className={`text-[11px] ${overdue ? 'text-[hsl(0_72%_40%)] font-medium' : 'text-muted-foreground'}`}>
                    Due {due}{overdue ? ' — overdue' : ''}
                  </span>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                {it.status === 'draft_patient' && (
                  <>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => copy(it.magic_token)}><Copy className="h-3.5 w-3.5 mr-1" /> Link</Button>
                    {canSendLinks && <Button size="sm" variant="outline" className="flex-1" onClick={() => resend(it)} data-testid={`nurse-resend-link-card-${it.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Resend</Button>}
                  </>
                )}
                <Button size="sm" className="flex-1" onClick={() => navigate(`/nurse/submission/${it.id}`)} data-testid={`nurse-open-card-${it.id}`}>Open</Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Desktop: table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="nurse-queue-table">
            <thead className="bg-card sticky top-0 border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Packet</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No submissions yet. Create a patient link to get started.</td></tr>
              )}
              {items.map((it) => {
                const pt = packetTypes.find(p => p.id === it.packet_type_id)?.label || it.packet_type_id;
                const due = it.delivery?.due_date || '';
                const overdue = it._overdue;
                return (
                  <tr key={it.id} className={`border-b hover:bg-muted/60 ${overdue ? 'border-l-2 border-l-[hsl(0_72%_52%)]' : ''}`} data-testid={`nurse-queue-row-${it.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium flex items-center gap-1.5">
                        {it.patient_form?.full_name || <span className="text-muted-foreground">(awaiting patient)</span>}
                        {it.auto_prep?.status === 'done' && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`nurse-ai-prepped-badge-${it.id}`}>
                            <Sparkles className="h-3 w-3" /> AI-prepped
                          </span>
                        )}
                        {(it._open_revisions || 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`nurse-revision-badge-${it.id}`}>
                            <AlertTriangle className="h-3 w-3" /> Change requested
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{it.patient_form?.employer_name || ''}</div>
                    </td>
                    <td className="px-3 py-2">{pt}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                    <td className="px-3 py-2">
                      {due ? (
                        <span className={`text-sm ${overdue ? 'text-[hsl(0_72%_40%)] font-medium' : ''}`}>
                          {overdue && <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                          {due}{overdue ? ' — Overdue' : ''}
                        </span>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{(it.updated_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        {it.status === 'draft_patient' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => copy(it.magic_token)} data-testid={`nurse-copy-link-${it.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Link</Button>
                            {canSendLinks && <Button size="sm" variant="outline" onClick={() => resend(it)} data-testid={`nurse-resend-link-${it.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Resend</Button>}
                          </>
                        )}
                        <Button size="sm" onClick={() => navigate(`/nurse/submission/${it.id}`)} data-testid={`nurse-open-submission-${it.id}`}>Open</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
